"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography } from "@repo/ui/aops-components/typography";
import type { DiscoveryPageQueryResult } from "@repo/sanity-utils/schema/types";
import styles from "./DiscoveryTabbedCarousel.module.css";
import layoutStyles from "../../layout.module.css";
import { ArrowButtons } from "./ArrowButtons/ArrowButtons";
import { CarouselContext, CarouselProvider } from "./CarouselContext";
import { useContext } from "react";
import TabButton from "./TabButton/TabButton";
import { AopsPortableText } from "../../PortableText/PortableTextComponents";
import Image from "next/image";
import SlideBreadcrumbs from "./SlideBreadcrumbs/SlideBreadcrumbs";
import { cn } from "@repo/ui/lib/utils";
import { PanelCornerDots } from "../../PanelCornerDots";
import { useCarouselSwipe } from "./useCarouselSwipe";
type DiscoveryPageBlocks = NonNullable<NonNullable<DiscoveryPageQueryResult>["pageContent"]>;
type DiscoveryTabbedCarouselBlock = Extract<DiscoveryPageBlocks[number], {
  _type: "discoveryTabbedCarouselBlock";
}>;
function slideTitle(slide: NonNullable<DiscoveryTabbedCarouselBlock["slides"]>[number]): string {
  if (slide.titleSource === "modality") {
    return slide.modality?.title ?? "";
  }
  return slide.title ?? "";
}
type DiscoveryTabbedCarouselProps = {
  tabbedCarousel: DiscoveryTabbedCarouselBlock;
  slideImageUrls: {
    tab?: string;
    body?: string;
    testimonial?: string[];
  }[];
};
function SlideTestimonialContent(t0) {
  const $ = _c(27);
  const {
    isActive,
    slide,
    testimonialImageUrls
  } = t0;
  let content = null;
  if (slide.testimonial?.detailType === "stats") {
    let t1;
    if ($[0] !== slide.testimonial?.stats) {
      t1 = slide.testimonial?.stats?.map(_temp);
      $[0] = slide.testimonial?.stats;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <div className={styles.slideTestimonialStats}>{t1}</div>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    content = t2;
  } else {
    if (slide.testimonial?.detailType === "images") {
      let t1;
      if ($[4] !== testimonialImageUrls) {
        t1 = testimonialImageUrls?.map(_temp2);
        $[4] = testimonialImageUrls;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      let t2;
      if ($[6] !== t1) {
        t2 = <div className={styles.slideTestimonialImages}>{t1}</div>;
        $[6] = t1;
        $[7] = t2;
      } else {
        t2 = $[7];
      }
      content = t2;
    } else {
      if (slide.testimonial?.detailType === "textList") {
        let t1;
        if ($[8] !== slide.testimonial?.textList) {
          t1 = slide.testimonial?.textList?.map(_temp3);
          $[8] = slide.testimonial?.textList;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        let t2;
        if ($[10] !== t1) {
          t2 = <ul className={styles.slideTestimonialTextList}>{t1}</ul>;
          $[10] = t1;
          $[11] = t2;
        } else {
          t2 = $[11];
        }
        content = t2;
      }
    }
  }
  const t1 = `var(${slide.accentColor})`;
  let t2;
  if ($[12] !== t1) {
    t2 = {
      "--accent-color": t1
    };
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const t3 = t2 as React.CSSProperties;
  let t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(styles.slideTestimonialCard, styles.slideContent);
    t5 = <PanelCornerDots dotClassName={styles.slideTestimonialDot} />;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] !== slide.testimonial?.source || $[17] !== slide.testimonial?.text) {
    t6 = slide.testimonial?.text && <div><Typography as="p" variant="bodyLarge">{slide.testimonial?.text}</Typography><Typography as="p" variant="tag" className={styles.slideTestimonialSource}>{slide.testimonial?.source}</Typography></div>;
    $[16] = slide.testimonial?.source;
    $[17] = slide.testimonial?.text;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== content || $[20] !== t6) {
    t7 = <div className={styles.slideTestimonialContent}>{t6}{content}</div>;
    $[19] = content;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PanelCornerDots dotClassName={styles.slideTestimonialDot} />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== isActive || $[24] !== t3 || $[25] !== t7) {
    t9 = <div style={t3} data-active={isActive} className={t4}>{t5}{t7}{t8}</div>;
    $[23] = isActive;
    $[24] = t3;
    $[25] = t7;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
}
function _temp3(text) {
  return <li key={text}><Typography as="span" variant="h5">{text}</Typography></li>;
}
function _temp2(url) {
  return <Image key={url} src={url} alt="" width={120} height={40} />;
}
function _temp(t0) {
  const {
    _key,
    value,
    description
  } = t0;
  return <div key={_key} className={styles.slideTestimonialStat}><Typography as="span" variant="stat" className={styles.slideTestimonialStatValue}>{value}</Typography><Typography as="span" variant="tag" className={styles.slideTestimonialStatDescription}>{description}</Typography></div>;
}
function DiscoveryTabbedCarouselWithContext(t0) {
  const $ = _c(50);
  const {
    tabbedCarousel,
    slideImageUrls
  } = t0;
  const {
    activeIndex
  } = useContext(CarouselContext);
  const swipeHandlers = useCarouselSwipe();
  let t1;
  if ($[0] !== tabbedCarousel.slides) {
    t1 = tabbedCarousel.slides ?? [];
    $[0] = tabbedCarousel.slides;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slides = t1;
  let t2;
  if ($[2] !== tabbedCarousel.eyebrowText) {
    t2 = tabbedCarousel.eyebrowText && <Typography as="p" variant="eyebrow" className={styles.eyebrow}>{tabbedCarousel.eyebrowText}</Typography>;
    $[2] = tabbedCarousel.eyebrowText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== tabbedCarousel.title) {
    t3 = <Typography as="h2" variant="h2" className={styles.sectionTitleText}>{tabbedCarousel.title}</Typography>;
    $[4] = tabbedCarousel.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== tabbedCarousel.subtitle) {
    t4 = tabbedCarousel.subtitle && <Typography as="p" variant="bodyLarge" className={styles.subtitle}>{tabbedCarousel.subtitle}</Typography>;
    $[6] = tabbedCarousel.subtitle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className={styles.sectionTitleTextRegion}>{t2}{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={styles.arrowButtonsRegion}><ArrowButtons /></div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <div className={styles.sectionTitleRegion}>{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== slideImageUrls || $[16] !== slides) {
    let t9;
    if ($[18] !== slideImageUrls) {
      t9 = (slide, index) => <TabButton key={slide._key} index={index} text={slideTitle(slide)} iconUrl={slideImageUrls[index]?.tab ?? null} accentColor={slide.accentColor} />;
      $[18] = slideImageUrls;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = slides.map(t9);
    $[15] = slideImageUrls;
    $[16] = slides;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className={styles.tabButtons}>{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== activeIndex || $[23] !== slides) {
    let t11;
    if ($[25] !== activeIndex) {
      t11 = (slide_0, index_0) => <div key={slide_0._key} data-active={index_0 === activeIndex} className={cn(styles.slideTitleRegion, styles.slideContent)}><Typography as="h3" variant="h4" className={styles.slideTitleText}>{slideTitle(slide_0)}</Typography><AopsPortableText value={slide_0.description} /></div>;
      $[25] = activeIndex;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    t10 = slides.map(t11);
    $[22] = activeIndex;
    $[23] = slides;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[27] !== slides.length) {
    t11 = <SlideBreadcrumbs numSlides={slides.length} />;
    $[27] = slides.length;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <div className={styles.slideTitleBlock}>{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== activeIndex || $[33] !== slideImageUrls || $[34] !== slides) {
    let t14;
    if ($[36] !== activeIndex || $[37] !== slideImageUrls) {
      t14 = (slide_1, index_1) => {
        if (slide_1.contentType === "image") {
          const slideImage = slideImageUrls[index_1]?.body;
          return slideImage ? <Image key={slide_1._key} src={slideImage} alt={slideTitle(slide_1)} width={640} height={480} sizes="(max-width: 1024px) 100vw, 720px" quality={100} data-active={index_1 === activeIndex} className={cn(styles.slideImage, styles.slideContent)} /> : <div key={slide_1._key} data-active={index_1 === activeIndex} className={cn(styles.slideImage, styles.slideContent)} />;
        } else {
          if (slide_1.contentType === "text") {
            return <SlideTestimonialContent key={slide_1._key} isActive={index_1 === activeIndex} slide={slide_1} testimonialImageUrls={slideImageUrls[index_1]?.testimonial ?? []} />;
          }
        }
      };
      $[36] = activeIndex;
      $[37] = slideImageUrls;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    t13 = slides.map(t14);
    $[32] = activeIndex;
    $[33] = slideImageUrls;
    $[34] = slides;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[39] !== swipeHandlers || $[40] !== t12 || $[41] !== t13) {
    t14 = <div className={styles.slideBody} {...swipeHandlers}>{t12}{t13}</div>;
    $[39] = swipeHandlers;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className={styles.mobileArrowButtonsRegion}><ArrowButtons /></div>;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t14 || $[45] !== t9) {
    t16 = <div className={styles.carouselContainer}>{t9}{t14}{t15}</div>;
    $[44] = t14;
    $[45] = t9;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16 || $[48] !== t7) {
    t17 = <section className={styles.carouselSection}><div className={layoutStyles.content}>{t7}{t16}</div></section>;
    $[47] = t16;
    $[48] = t7;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
}
export function DiscoveryTabbedCarousel(t0) {
  const $ = _c(6);
  const {
    tabbedCarousel,
    slideImageUrls
  } = t0;
  const t1 = tabbedCarousel.slides?.length ?? 0;
  let t2;
  if ($[0] !== slideImageUrls || $[1] !== tabbedCarousel) {
    t2 = <DiscoveryTabbedCarouselWithContext tabbedCarousel={tabbedCarousel} slideImageUrls={slideImageUrls} />;
    $[0] = slideImageUrls;
    $[1] = tabbedCarousel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <CarouselProvider slideCount={t1}>{t2}</CarouselProvider>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}